/**
 * Shared helpers to derive or normalize locale from pathname or string.
 * Used by navbar, language selector, and any component that needs a reliable display locale.
 */

export type SupportedLocale = 'en' | 'fi' | 'sv';

const SUPPORTED_LOCALES: readonly SupportedLocale[] = ['en', 'fi', 'sv'];

/**
 * Returns the locale from the first path segment if it is en/fi/sv, otherwise null.
 * Pathname can be with or without leading slash (e.g. "/fi/tools" or "fi/tools").
 */
export function getLocaleFromPathname(pathname: string): SupportedLocale | null {
  const first = pathname.replace(/^\/+/, '').split('/').filter(Boolean)[0]?.toLowerCase();
  return first && (SUPPORTED_LOCALES as readonly string[]).includes(first)
    ? (first as SupportedLocale)
    : null;
}

/**
 * Normalizes any string (e.g. intl.locale, pageContext.locale) to a supported locale.
 * Handles BCP 47 tags (fi-FI, sv-SE, en-US) and undefined.
 */
export function normalizeSupportedLocale(value: string | undefined | null): SupportedLocale {
  if (value == null || typeof value !== 'string') return 'en';
  const lower = value.toLowerCase();
  if (lower.startsWith('fi')) return 'fi';
  if (lower.startsWith('sv')) return 'sv';
  return 'en';
}
